/**
 * Image source picker — modal that lets the user choose an image to insert,
 * either by uploading a file or by picking one of an order's / customer's /
 * product's existing attachments.
 *
 * Static API:
 *   ImageSourcePickerView.open({
 *     title:   <string?>,
 *     sources: [{ key, label, loader(first, pageSize) -> promise<AttachmentModel[]> }],
 *     onPick:  function(url, fileName)   // url is ready to hand to fabric.Image.fromURL
 *   });
 *
 * An "Upload" tab is always present in addition to the provided sources.
 */
define('imageSourcePickerView',[
  'module',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'template!imageSourcePickerView'
], function (
  Module,
  Marionette,
  _,
  $,
  Settings,
  Tpl
) {
  'use strict';

  var PAGE_SIZE = 24;
  // Attachments fabric can place — either directly (raster/svg) or after the
  // file proxy rasterizes them (pdf/ai/eps). Matches the attachment component's
  // `compositionImageExtensions`.
  var INSERTABLE_EXT = /\.(png|jpe?g|gif|bmp|webp|svg|pdf|ai|eps)$/i;

  function fileExt(name) {
    if (!name) {
      return '';
    }
    var parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  // fileLink looks like "files/getfile/<uid>?params=t&action=preview&tokenId=...".
  // Resolution mirrors app/modules/components/attachments/attachment/view.js:
  //   pdf/ai/eps -> keep &action=preview so the proxy returns a rendered image
  //   svg        -> drop the action; the browser renders SVG directly
  //   raster     -> a (re)sized render for the thumbnail, the original for insert

  function thumbUrl(attachment) {
    // Some sources (e.g. PIM products) supply an already-absolute URL.
    var direct = attachment.get('url');
    if (direct) {
      return direct;
    }
    var link = attachment.get('fileLink');
    if (!link) {
      return '';
    }
    var ext = fileExt(attachment.get('fileName'));
    if (ext === 'pdf' || ext === 'ai' || ext === 'eps') {
      return Settings.url('base', link);
    }
    if (ext === 'svg') {
      return Settings.url('base', link.replace('&action=preview', ''));
    }
    return Settings.url('base', link.replace('&action=preview', '&action=res'));
  }

  // The picked URL is drawn onto the PDF editor's <canvas>, so an image whose
  // host sends no CORS headers would taint the canvas and break the PDF export.
  // Route any absolute URL on a different origin than the dashboard through the
  // backend proxy (a compuzz origin that does send CORS), leaving same-origin /
  // relative URLs — and URLs already pointing at the proxy — untouched.
  function proxiedImageUrl(rawUrl) {
    if (!rawUrl || !/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }
    var origin = window.location.protocol + '//' + window.location.host;
    if (rawUrl === origin || rawUrl.indexOf(origin + '/') === 0) {
      return rawUrl;
    }
    if (rawUrl.indexOf('/urlproxy?') !== -1) {
      return rawUrl;
    }
    // Settings.url does not encode param values, so encode the URL ourselves.
    return Settings.url('compuzz2', 'urlproxy', { url: encodeURIComponent(rawUrl) });
  }

  // URL handed to fabric.Image.fromURL: full quality where the browser can decode
  // it, the proxy-rasterized preview for binary page/vector formats it cannot.
  // Cross-origin results are routed through the proxy so the canvas stays clean.
  function insertUrl(attachment) {
    var direct = attachment.get('url');
    if (direct) {
      return proxiedImageUrl(direct);
    }
    var link = attachment.get('fileLink');
    if (!link) {
      return '';
    }
    var ext = fileExt(attachment.get('fileName'));
    if (ext === 'pdf' || ext === 'ai' || ext === 'eps') {
      return proxiedImageUrl(Settings.url('base', link));
    }
    return proxiedImageUrl(Settings.url('base', link.replace('&action=preview', '')));
  }

  var View = Marionette.ItemView.extend({
    template: Tpl,
    className: 'image-source-picker-view',

    ui: {
      tabs: '.isp-tab',
      panel: '.isp-panel',
      uploadInput: '.isp-upload-input'
    },

    events: {
      'click .isp-tab': 'onTabClick',
      'click .isp-upload-area': 'onUploadAreaClick',
      'change @ui.uploadInput': 'onUploadChange',
      'click .isp-item': 'onItemClick',
      'click .isp-more': 'onLoadMore',
      'click .isp-cancel': 'onCancel'
    },

    initialize: function (options) {
      this.options = options || {};
      this.sources = this.options.sources || [];
      // key -> { items: [AttachmentModel], first: int, done: bool, loading: bool }
      this._state = {};
    },

    serializeData: function () {
      return {
        sources: _.map(this.sources, function (s) {
          return { key: s.key, label: s.label };
        })
      };
    },

    onRender: function () {
      this.activateTab('upload');
    },

    onTabClick: function (e) {
      this.activateTab($(e.currentTarget).attr('data-key'));
    },

    activateTab: function (key) {
      this.ui.tabs.removeClass('active');
      this.ui.tabs.filter('[data-key="' + key + '"]').addClass('active');
      this.activeKey = key;

      if (key === 'upload') {
        this.renderUploadPanel();
      } else {
        this.renderSourcePanel(key);
      }
    },

    renderUploadPanel: function () {
      this.ui.panel.html(
        '<div class="isp-upload-area">' +
        '<i class="mdi mdi-file-file-upload"></i>' +
        '<span>' + _.i18n('imageSourcePicker.uploadHint', null, 'Click to choose an image to upload') + '</span>' +
        '</div>'
      );
    },

    onUploadAreaClick: function () {
      this.ui.uploadInput.val('');
      this.ui.uploadInput.click();
    },

    onUploadChange: function (e) {
      var file = e.target.files && e.target.files[0];
      if (!file) {
        return;
      }
      // Blob URLs are same-origin, so the canvas stays untainted on export.
      var url = window.URL.createObjectURL(file);
      this.pick(url, file.name);
    },

    getState: function (key) {
      if (!this._state[key]) {
        this._state[key] = { items: [], first: 0, done: false, loading: false };
      }
      return this._state[key];
    },

    renderSourcePanel: function (key) {
      var state = this.getState(key);
      if (state.items.length === 0 && !state.done) {
        this.ui.panel.html('<div class="isp-loading">' + _.i18n('common.loading', null, 'Loading…') + '</div>');
        this.loadMore(key);
        return;
      }
      this.renderItems(key);
    },

    loadMore: function (key) {
      var source = _.findWhere(this.sources, { key: key });
      var state = this.getState(key);
      if (!source || !source.loader || state.loading || state.done) {
        return;
      }
      state.loading = true;

      $.when(source.loader(state.first, PAGE_SIZE)).done(_.bind(function (items) {
        items = items || [];
        // Only keep attachments that can end up as an image on the canvas.
        var images = _.filter(items, function (a) {
          return INSERTABLE_EXT.test(a.get('fileName') || '');
        });
        state.items = state.items.concat(images);
        state.first += items.length;
        if (items.length < PAGE_SIZE) {
          state.done = true;
        }
        state.loading = false;
        if (this.activeKey === key) {
          this.renderItems(key);
        }
      }, this)).fail(_.bind(function () {
        state.loading = false;
        state.done = true;
        if (this.activeKey === key) {
          this.ui.panel.html('<div class="isp-empty">' +
            _.i18n('imageSourcePicker.loadError', null, 'Could not load attachments.') + '</div>');
        }
      }, this));
    },

    renderItems: function (key) {
      var state = this.getState(key);

      if (state.items.length === 0) {
        this.ui.panel.html('<div class="isp-empty">' +
          _.i18n('imageSourcePicker.empty', null, 'No images here.') + '</div>');
        return;
      }

      var html = '<div class="isp-grid">';
      _.each(state.items, function (a, i) {
        var name = a.get('fileName') || '';
        var thumb = thumbUrl(a);
        html += '<button type="button" class="isp-item" data-key="' + key + '" data-index="' + i + '">' +
          '<div class="isp-thumb"><img src="' + thumb + '"/></div>' +
          '<span class="isp-name" title="' + _.escape(name) + '">' + _.escape(name) + '</span>' +
          '</button>';
      });
      html += '</div>';
      if (!state.done) {
        html += '<div class="isp-more-row"><button type="button" class="btn btn-default isp-more">' +
          _.i18n('common.loadMore', null, 'Load more') + '</button></div>';
      }
      this.ui.panel.html(html);
    },

    onLoadMore: function () {
      this.loadMore(this.activeKey);
    },

    onItemClick: function (e) {
      var el = $(e.currentTarget);
      var key = el.attr('data-key');
      var index = parseInt(el.attr('data-index'), 10);
      var state = this.getState(key);
      var attachment = (!isNaN(index)) ? state.items[index] : null;
      if (!attachment) {
        return;
      }
      this.pick(insertUrl(attachment), attachment.get('fileName'));
    },

    pick: function (url, fileName) {
      var onPick = this.options.onPick;
      this.closeWindow();
      if (url && typeof onPick === 'function') {
        try {
          onPick(url, fileName);
        } catch (err) {
          if (window.console && console.error) {
            console.error('image source picker onPick threw:', err);
          }
        }
      }
    },

    onCancel: function () {
      this.closeWindow();
    },

    closeWindow: function () {
      if (this.windowView) {
        this.windowView.close();
      }
    }
  });

  View.open = function (data) {
    require([
      'windows',
      'imageSourcePickerView'
    ], function (Windows, ViewCtor) {
      Windows.open({
        view: function () { return new ViewCtor(data); },
        id: 'image-source-picker:' + Date.now(),
        title: (data && data.title) || _.i18n('imageSourcePicker.title', null, 'Insert image'),
        modal: true,
        width: 680,
        height: 560
      });
    });
  };

  Module.exports = View;
});

